import { useState } from "react";
import { useTranslation } from "react-i18next";
import { baseApi } from "@/shared/api";
import { useAppDispatch } from "@/shared/hooks";
import { extractErrorMessage } from "@/shared/utils";
import { isBudgetLimitErrorV2, isOverrideableError } from "@/features/accounting/smeta/types";
import { useOverrideState } from "@/features/accounting/smeta/hooks/useOverrideState";
import { extractSmetaErrorMessage } from "@/features/accounting/smeta/utils/errorDispatch";
import type { OverrideRequest } from "@/features/accounting/smeta/hooks/useOverrideState";
import {
  useCreatePurchaseMutation,
  useApprovePurchaseMutation,
  useMarkPaidPurchaseMutation,
  useDeletePurchaseMutation,
  useExtractOCRMutation,
  usePartialUpdatePurchaseMutation,
} from "../api";
import type { CreatePurchaseDto, UpdatePurchaseDto, ApprovePurchaseBody } from "../types";
import type { PromptState } from "./usePurchasesModals";

export interface UsePurchasesMutationsReturn {
  creating: boolean;
  updating: boolean;
  approvingId: number | null;
  markingPaidId: number | null;
  deletingId: number | null;
  extractingOCR: boolean;
  isApproving: boolean;
  isMarkingPaid: boolean;
  isDeleting: boolean;
  handleCreate: (dto: CreatePurchaseDto) => Promise<void>;
  handleUpdate: (id: number, dto: UpdatePurchaseDto) => Promise<void>;
  handleApprove: (id: number, body?: ApprovePurchaseBody) => Promise<void>;
  handleMarkPaid: (id: number, paid_date?: string) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  handleExtractOCR: (file: File) => Promise<any>;
  setPrompt: (state: PromptState | null) => void;
  overrideV2Pending: OverrideRequest<{ id: number }> | null;
  handleOverrideV2Confirm: (overrideFields: { override_reason: string }) => Promise<void>;
  closeOverrideV2: () => void;
}

export function usePurchasesMutations(setPrompt: (state: PromptState | null) => void): UsePurchasesMutationsReturn {
  const { t } = useTranslation("PurchasesPage");
  const { t: tSmeta } = useTranslation("SmetaPage");
  const dispatch = useAppDispatch();
  const overrideV2 = useOverrideState<{ id: number }>();

  const [createPurchase] = useCreatePurchaseMutation();
  const [updatePurchase] = usePartialUpdatePurchaseMutation();
  const [approvePurchase] = useApprovePurchaseMutation();
  const [markPaidPurchase] = useMarkPaidPurchaseMutation();
  const [deletePurchase] = useDeletePurchaseMutation();
  const [extractOCR] = useExtractOCRMutation();

  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [markingPaidId, setMarkingPaidId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [extractingOCR, setExtractingOCR] = useState(false);

  const isApproving = approvingId !== null;
  const isMarkingPaid = markingPaidId !== null;
  const isDeleting = deletingId !== null;

  async function handleCreate(dto: CreatePurchaseDto) {
    if (creating) return;
    try {
      setCreating(true);
      await createPurchase(dto).unwrap();
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.createSuccessTitle"),
        text: t("messages.createSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.createErrorText", t),
        variant: "error",
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleUpdate(id: number, dto: UpdatePurchaseDto) {
    if (updating) return;
    try {
      setUpdating(true);
      await updatePurchase({ id, body: dto }).unwrap();
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.updateSuccessTitle"),
        text: t("messages.updateSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.createErrorText", t),
        variant: "error",
      });
    } finally {
      setUpdating(false);
    }
  }

  async function handleApprove(id: number, body?: ApprovePurchaseBody) {
    if (isApproving) return;
    try {
      setApprovingId(id);
      await approvePurchase({ id, body }).unwrap();
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.approveSuccessTitle"),
        text: t("messages.approveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      if (isBudgetLimitErrorV2(err)) {
        if (err.data.failure_type === "invalid_input") {
          setPrompt({ title: t("messages.errorTitle"), text: tSmeta("errors.invalid_input"), variant: "error" });
          return;
        }
        if (isOverrideableError(err.data)) {
          overrideV2.request(err.data, { id });
          return;
        }
        setPrompt({
          title: tSmeta("messages.errorTitle"),
          text: extractSmetaErrorMessage(err.data, tSmeta),
          variant: "error",
        });
        return;
      }
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.approveErrorText", t),
        variant: "error",
      });
    } finally {
      setApprovingId(null);
    }
  }

  async function handleOverrideV2Confirm(overrideFields: { override_reason: string }) {
    if (!overrideV2.pending || isApproving) return;
    const { id } = overrideV2.pending.retryArgs;
    overrideV2.clear();
    try {
      setApprovingId(id);
      await approvePurchase({ id, body: { override_reason: overrideFields.override_reason } }).unwrap();
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.approveSuccessTitle"),
        text: t("messages.approveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      if (isBudgetLimitErrorV2(err)) {
        if (err.data.failure_type === "invalid_input") {
          setPrompt({ title: t("messages.errorTitle"), text: tSmeta("errors.invalid_input"), variant: "error" });
          return;
        }
        if (isOverrideableError(err.data)) {
          overrideV2.request(err.data, { id });
          return;
        }
        setPrompt({ title: tSmeta("messages.errorTitle"), text: extractSmetaErrorMessage(err.data, tSmeta), variant: "error" });
      } else {
        setPrompt({ title: t("messages.errorTitle"), text: extractErrorMessage(err, "messages.approveErrorText", t), variant: "error" });
      }
    } finally {
      setApprovingId(null);
    }
  }

  async function handleMarkPaid(id: number, paid_date?: string) {
    if (isMarkingPaid) return;
    try {
      setMarkingPaidId(id);
      await markPaidPurchase({ id, paid_date }).unwrap();
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.markPaidSuccessTitle"),
        text: t("messages.markPaidSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.markPaidErrorText", t),
        variant: "error",
      });
    } finally {
      setMarkingPaidId(null);
    }
  }

  async function handleDelete(id: number) {
    if (isDeleting) return;
    try {
      setDeletingId(id);
      await deletePurchase(id).unwrap();
      await Promise.all(dispatch(baseApi.util.getRunningQueriesThunk()));
      setPrompt({
        title: t("messages.deleteSuccessTitle"),
        text: t("messages.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.deleteErrorText", t),
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }

  async function handleExtractOCR(file: File) {
    if (extractingOCR) return;
    try {
      setExtractingOCR(true);
      const result = await extractOCR(file).unwrap();
      return result;
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.ocrErrorText", t),
        variant: "error",
      });
      throw err;
    } finally {
      setExtractingOCR(false);
    }
  }

  return {
    creating,
    updating,
    approvingId,
    markingPaidId,
    deletingId,
    extractingOCR,
    isApproving,
    isMarkingPaid,
    isDeleting,
    handleCreate,
    handleUpdate,
    handleApprove,
    handleMarkPaid,
    handleDelete,
    handleExtractOCR,
    setPrompt,
    overrideV2Pending: overrideV2.pending,
    handleOverrideV2Confirm,
    closeOverrideV2: overrideV2.clear,
  };
}

